import React, { useEffect, useState } from 'react'
import { useForm, NestedValue } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Box, Divider, makeStyles, Typography } from '@material-ui/core'
import { FormGroup } from '../../form/FormGroup'
import { ControlledTextField } from '../../form/ControlledTextField'
import { WizardStep, WizardStepProps } from '../../form/WizardStep'
import { validators } from '../../../validation/validators'
import { FormValues as FullFormValues } from './form-values'
import {
  HourSeriesCollectionFragment,
  useGlobalHourSeriesCollectionsQuery,
} from '../../../generated/graphql'
import { hasBuildingPartTemplates } from '../../../hour-series-collection/hour-series-collection-utility'
import { ControlledCreatePartsEditor } from '../../form/ControlledCreatePartsEditor'
import { ControlledUpdatePartsEditor } from '../../form/ControlledUpdatePartsEditor'

type InternalFormValues = Pick<FullFormValues, 'area'> & {
  createdParts: NestedValue<FullFormValues['createdParts']>
  updatedParts: NestedValue<FullFormValues['updatedParts']>
}

type FormValues = Pick<FullFormValues, 'area' | 'createdParts' | 'updatedParts'>

type Props = WizardStepProps<FullFormValues> & {
  onSubmit: (values: FormValues) => void
}

const useStyles = makeStyles((theme) => ({
  explanationText: {
    color: theme.palette.grey[500],
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
}))

export function PartsStep(props: Props): React.ReactElement {
  const { defaultValues, onSubmit } = props

  const { control, handleSubmit, errors } = useForm<InternalFormValues>({
    defaultValues,
  })
  const { t } = useTranslation()
  const classes = useStyles()

  const [defaultTemplate, setDefaultTemplate] =
    useState<HourSeriesCollectionFragment>()

  const [templates, setTemplates] = useState<HourSeriesCollectionFragment[]>()

  const { data, loading } = useGlobalHourSeriesCollectionsQuery()

  useEffect(() => {
    const partTemplates = data?.globalHourSeriesCollections.filter(
      hasBuildingPartTemplates,
    )
    setTemplates(partTemplates)
    setDefaultTemplate(partTemplates?.[0])
  }, [data, loading])

  const onPartsStepSubmit = (values: FormValues) => {
    // ControlledPartsEditor, which uses useFieldArray internally, removes the array if the last element is removed.
    // In this case, add the empty array back to ensure it is not overwritten with the default values when moving on to the next step in the Wizard.
    const correctedValues = {
      ...values,
      createdParts: values.createdParts ?? [],
      updatedParts: values.updatedParts ?? [],
    }
    onSubmit(correctedValues)
  }

  return (
    <WizardStep
      {...props}
      title={t('createBuilding.pages.parts.title')}
      onSubmit={handleSubmit(onPartsStepSubmit)}
    >
      <FormGroup>
        <ControlledTextField
          control={control}
          name="area"
          rules={{
            ...validators.required,
            ...validators.decimal,
          }}
          error={errors.area}
          label={t('createBuilding.pages.parts.totalArea')}
        />
      </FormGroup>

      <Box py={4}>
        <Divider />
      </Box>

      <Typography variant="subtitle1">
        {t('createBuilding.pages.parts.partsHeader')}
      </Typography>

      <Typography variant="body1" className={classes.explanationText}>
        {t('createBuilding.pages.parts.partsExplanation')}
      </Typography>

      <ControlledUpdatePartsEditor
        control={control}
        name="updatedParts"
        error={errors.updatedParts}
      />

      <ControlledCreatePartsEditor
        control={control}
        name="createdParts"
        error={errors.createdParts}
        templates={templates}
        defaultTemplate={defaultTemplate}
      />
    </WizardStep>
  )
}
